import React from 'react';
import type { Agent } from '@/types/agent';
import { STATUS_COLORS } from '@/utils/colors';
import { formatStars, formatTokens } from '@/utils/formatters';

const STATUS_LABELS: Record<string, string> = {
  idle: 'En attente',
  working: 'En cours',
  moving: 'En déplacement',
  thinking: 'Réflexion',
  waiting_validation: 'Validation requise',
  offline: 'Hors ligne',
  error: 'Erreur',
  meeting: 'En réunion',
};

interface Props {
  agent: Agent;
  selected: boolean;
  onClick: () => void;
}

export const AgentRosterItem: React.FC<Props> = ({ agent, selected, onClick }) => {
  const statusColor = STATUS_COLORS[agent.status] ?? '#4a5568';
  const isWarning = agent.status === 'waiting_validation' || agent.status === 'error';

  return (
    <button
      onClick={onClick}
      className={[
        'w-full text-left px-3 py-2 rounded transition-all duration-150 border',
        selected
          ? 'bg-bg-elevated border-opacity-60'
          : 'bg-transparent border-transparent hover:bg-bg-elevated/50 hover:border-bg-border',
        isWarning ? 'animate-pulse' : '',
      ].join(' ')}
      style={selected ? { borderColor: agent.color + '60' } : {}}
    >
      <div className="flex items-center gap-2">
        {/* Avatar (cercle néon) */}
        <div
          className="relative flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-pixel text-xs"
          style={{
            background: agent.color + '22',
            border: `2px solid ${agent.color}`,
            boxShadow: `0 0 8px ${agent.color}40`,
            color: agent.color,
          }}
        >
          {agent.codename.slice(0, 2)}
          {/* Point de statut */}
          <span
            className="absolute bottom-0 right-0 w-2 h-2 rounded-full border border-bg-panel"
            style={{ background: statusColor, boxShadow: `0 0 4px ${statusColor}` }}
          />
        </div>

        {/* Infos */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <span className="font-pixel text-xs truncate" style={{ color: agent.color }}>
              {agent.codename}
            </span>
            <span className="font-mono text-xs text-neon-amber flex-shrink-0">
              Lv{agent.stats.level}
            </span>
          </div>
          <div className="font-mono text-xs text-gray-500 truncate">{agent.roleName}</div>
          <div className="flex items-center justify-between mt-0.5">
            <span
              className="font-mono text-xs"
              style={{ color: statusColor }}
            >
              {STATUS_LABELS[agent.status] ?? agent.status}
            </span>
            <span className="font-mono text-xs text-gray-600">
              {formatTokens(agent.tokensBurned)}t
            </span>
          </div>
        </div>
      </div>

      {/* Barre d'XP */}
      {selected && (
        <div className="mt-2">
          <div className="flex justify-between text-xs font-mono text-gray-600 mb-0.5">
            <span>{formatStars(agent.stats.stars)}</span>
            <span>{agent.stats.xp}/{agent.stats.xpToNext} XP</span>
          </div>
          <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(agent.stats.xp / agent.stats.xpToNext) * 100}%`,
                background: agent.color,
                boxShadow: `0 0 4px ${agent.color}`,
              }}
            />
          </div>
        </div>
      )}
    </button>
  );
};
